import { useMutation, useQuery } from "@tanstack/react-query"
import styles from "../../styles.module.scss"
import { observer } from "mobx-react-lite"
import { getCruiseLines } from "../../../../../../../services/firebase-functions/dictionaries"
import { DefaultSpinner } from "../../../../../../loaders/DefaultSpinner"
import classNames from "classnames"
import { CruiseLineId } from "../../../../../../../../../firebase/functions/src/libs/cruisen-contract/cruises/cruise-line.interface"
import { makeAutoObservable } from "mobx"
import { useEffect, useMemo } from "react"
import { AccountModalFooter } from "../../components/Footer"
import { upsertAgentProfile } from "../../../../../../../services/firebase-functions/agentProfile"
import { useStores } from "../../../../../../../models/helpers/useStores"

class CruiseLinesTabStore {
  preferredCruiseLineIds: CruiseLineId[] = []

  constructor(preferredCruiseLineIds: CruiseLineId[]) {
    this.preferredCruiseLineIds = preferredCruiseLineIds

    makeAutoObservable(this)
  }

  togglePreferredCruiseLineId(cruiseLineId: CruiseLineId) {
    this.preferredCruiseLineIds = this.preferredCruiseLineIds.includes(cruiseLineId)
      ? this.preferredCruiseLineIds.filter((id) => id !== cruiseLineId)
      : [...this.preferredCruiseLineIds, cruiseLineId]
  }
}

export const CruiseLinesTab = observer(
  ({
    closeModal,
    setIsModalWarningVisible,
    setIsContentChanged,
  }: {
    closeModal: () => void
    setIsModalWarningVisible: (value: boolean) => void
    setIsContentChanged: (value: boolean) => void
  }) => {
    const rootStore = useStores()
    const { loggedInUser, agentProfile } = rootStore
    const prefferedCruiseLineIds = agentProfile?.preferredCruiseLineIds
      ? [...agentProfile.preferredCruiseLineIds]
      : []

    const store = useMemo(() => new CruiseLinesTabStore(prefferedCruiseLineIds), [])
    const { data: cruiseLines, isLoading: isLoadingCruiseLines } = useQuery({
      queryKey: ["cruiseLines"],
      queryFn: () => getCruiseLines(),
    })

    const updatedPreferredCruiseLineIds = Array.from(store.preferredCruiseLineIds)
    const isDirty =
      JSON.stringify(prefferedCruiseLineIds.sort()) !==
      JSON.stringify(updatedPreferredCruiseLineIds.sort())

    const { mutate: updatePreferredCruiseLineIds, isPending } = useMutation({
      mutationFn: async () => {
        const updatedAgentProfile = await upsertAgentProfile({
          agentProfile: {
            id: agentProfile.id,
            userId: loggedInUser.id,
            preferredCruiseLineIds: updatedPreferredCruiseLineIds,
          },
        })

        return updatedAgentProfile
      },
      onSuccess: (updatedAgentProfile) => {
        if (updatedAgentProfile) {
          rootStore.setProp("agentProfile", updatedAgentProfile)
        }
      },
    })

    useEffect(() => {
      setIsContentChanged(isDirty)
    }, [isDirty])

    const handleCloseModal = () => {
      if (isDirty) {
        setIsModalWarningVisible(true)
        return
      }

      closeModal()
    }

    if (isLoadingCruiseLines || !cruiseLines.length) {
      return (
        <div className={classNames(styles.loadingContainer, styles.loadingContainerWithPadding)}>
          <DefaultSpinner />
        </div>
      )
    }

    return (
      <>
        <div className={styles.accountModalBody}>
          <div className={styles.cruiseLinesContainer}>
            <div className={styles.sectionInfo}>
              <p className={styles.sectionTitle}>Preferred cruise lines</p>
              <p className={styles.sectionDescription}>
                Your customers will only see the cruise lines you select here.
              </p>
            </div>
            <div className={styles.cruiseLinesList}>
              {cruiseLines.map((cruiseLine) => (
                <button
                  className={classNames(
                    styles.cruiseLineItem,
                    store.preferredCruiseLineIds.includes(cruiseLine.id) &&
                      styles.cruiseLineItemSelected,
                  )}
                  key={cruiseLine.id}
                  onClick={() => store.togglePreferredCruiseLineId(cruiseLine.id)}
                >
                  {cruiseLine.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <AccountModalFooter
          isPending={isPending}
          isSaveDisabled={!isDirty}
          onCancel={() => handleCloseModal()}
          onSave={() => updatePreferredCruiseLineIds()}
        />
      </>
    )
  },
)
